import { refreshQueries } from './refreshQueries'
import { refreshEpisodeQueries } from '@/features/episode-library/refreshEpisodeQueries'
import { computed, onMounted, onUnmounted, unref, watch, type MaybeRef } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { tryoutContextAPI } from '@/api'
import { appSocket } from '@/services/socket'
import { useAuthStore } from '@/stores/auth'

export type TryoutContextDomain =
  'attendance' |
  'attributes' |
  'comments' |
  'events' |
  'feedbackSelections' |
  'participants' |
  'scores' |
  'sessions' |
  'teamAllocations' |
  string

export interface TryoutContextChangedPayload {
  type?: string
  domain?: TryoutContextDomain
  clubId: string
  seasonId: string
  tryoutId: string
  eventId?: string
  resourceId?: string | null
  participantId?: string | null
  sessionId?: string
  operation?: 'created' | 'updated' | 'deleted' | 'reordered' | 'completed' | 'reset' | string
  updatedAt?: string
  source?: string
  metadata?: Record<string, unknown>
}


export const tryoutContextQueryKey = (clubId: string, seasonId: string, tryoutId: string) => [
  'tryoutContext',
  clubId,
  seasonId,
  tryoutId,
]

// Keep the event prefix for websocket invalidation; permissions belong to a viewer.
export const tryoutContextViewerQueryKey = (clubId: string, seasonId: string, tryoutId: string, viewerId: string) => [
  ...tryoutContextQueryKey(clubId, seasonId, tryoutId), viewerId,
]

export const tryoutParticipantsQueryKey = (clubId: string, seasonId: string, tryoutId: string) => [
  'tryoutParticipants',
  clubId,
  seasonId,
  tryoutId,
]

export const tryoutCaptureQueryKey = (clubId: string, seasonId: string, tryoutId: string) => [
  'tryoutCapture',
  clubId,
  seasonId,
  tryoutId,
]

export function useTryoutContext(
  clubId: MaybeRef<string>,
  seasonId: MaybeRef<string>,
  tryoutId: MaybeRef<string>,
  enabled: MaybeRef<boolean> = true
) {
  const auth = useAuthStore()
  const viewerId = computed(() => String(auth.user?.details?.id || auth.user?.id || ''))
  return useQuery({
    queryKey: computed(() => tryoutContextViewerQueryKey(unref(clubId), unref(seasonId), unref(tryoutId), viewerId.value)),
    gcTime: 0,
    queryFn: ({ signal, queryKey }) => tryoutContextAPI.loadContext(queryKey[2]!, queryKey[3]!, queryKey[1]!, signal),
    enabled: computed(() => Boolean(unref(clubId) && unref(seasonId) && unref(tryoutId) && viewerId.value && !auth.switchingIdentity && unref(enabled))),
  })
}

export function useTryoutSessionInvalidation(
  clubId: MaybeRef<string>,
  seasonId: MaybeRef<string>,
  tryoutId: MaybeRef<string>
) {
  const queryClient = useQueryClient()
  const room = computed(() => unref(tryoutId) ? `tryout-context-${unref(tryoutId)}` : '')

  const invalidate = (queryKey: unknown[]) => {
    if(queryKey[0]==='tryoutCapture'){refreshQueries(queryClient,['tryoutCaptureParticipant',...queryKey.slice(1)]);refreshQueries(queryClient,['tryoutCaptureWorkspace',...queryKey.slice(1)])}
    return refreshQueries(queryClient, queryKey)
  }

  const invalidateParticipantDetail = (participantId: string | null | undefined) => {
    if (!participantId) return
    invalidate(['tryoutParticipant', unref(clubId), unref(seasonId), unref(tryoutId), participantId])
  }

  const handleContextChanged = (payload: TryoutContextChangedPayload) => {
    if (!matchesCurrentTryout(payload, unref(clubId), unref(seasonId), unref(tryoutId))) return
    invalidate(['eventNotifications', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['journalRewards', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['scoresheetScans', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['athleteFeedback', unref(clubId), unref(seasonId), unref(tryoutId)])
    if (['events','sessions','participants','attendance'].includes(payload.domain || '')) invalidate(['sessionReminders', unref(clubId), unref(seasonId), unref(tryoutId)])
    if (['events', 'roles'].includes(payload.domain || '')) invalidate(['eventWeightingProfile', unref(clubId), unref(seasonId), unref(tryoutId)])
    if (payload.domain === 'events') { invalidate(['tryoutRegistration', unref(tryoutId)]); invalidate(['clubStripeStatus', unref(clubId)]); invalidate(['registrationCoaches', unref(tryoutId)]); invalidate(['publicRegistrationCoaches', unref(tryoutId)]) }
    if (['comments', 'events', 'participants', 'sessions', 'attendance', 'teamAllocations', 'feedbackSelections'].includes(payload.domain || '')) {
      invalidate(['parentChild'])
    }
    if (['comments', 'events', 'participants', 'sessions'].includes(payload.domain || '')) {
      invalidate(['registrationChildren', unref(tryoutId)])
      invalidate(['tryoutRegistration', unref(tryoutId)])
      invalidate(['parentConversations'])
      invalidate(['parentConfirmations'])
      invalidate(['parentChildren'])
      invalidate(['parentEntrySharing'])
    }
    invalidate(['threatAnalysis', unref(clubId), unref(seasonId)])
    if (payload.source === 'scoutingV2') {
      invalidate(['scouting', unref(clubId), unref(seasonId), unref(tryoutId)])
      invalidate(['parentScouting'])
    }
    if (['scores', 'feedbackSelections', 'teamAllocations', 'participants', 'sessions', 'events', 'roles', 'attendance'].includes(payload.domain || '')) {
      invalidate(['assessmentReadiness', unref(clubId), unref(seasonId), unref(tryoutId)])
      invalidate(['assessmentFocus', unref(clubId), unref(seasonId), unref(tryoutId)])
    }
    if (['scores', 'feedbackSelections', 'participants', 'sessions', 'events', 'attributes', 'teamAllocations', 'recommendations', 'roles'].includes(payload.domain || '')) {
      invalidate(['tryoutCaptureAnalysis', unref(clubId), unref(seasonId), unref(tryoutId)])
    }
    invalidate(['captureSelectorGroups', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['spaceMembershipHistory', unref(clubId), unref(seasonId), unref(tryoutId)])
    if (['participants', 'teamAllocations', 'sessions'].includes(payload.domain || '')) invalidate(['participantCutHistory', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['manualParticipantAssociations', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['sessionReminders', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['demoJournalPreview', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['programReview', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['tryoutFeatures', unref(seasonId), unref(tryoutId)])
    invalidate(['sessionRecommendationTargets', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['eventAssessment', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['eventRubricLifecycle', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['assessmentStandards', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['programSettings', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['tryoutSetupActivities', unref(seasonId), unref(tryoutId)])
    if (payload.domain === 'sessions') {
      invalidate(['tryoutSetup', unref(seasonId), unref(tryoutId)])
    } else for (const key of ['registrationWelcome', 'tryoutSetup', 'tryoutSetupFees', 'tryoutSetupAttributes', 'tryoutSetupSessions', 'tryoutSetupSpaces', 'tryoutSetupTeams', 'tryoutSetupStaff', 'tryoutSetupEmails', 'tryoutSetupCriteria', 'tryoutSetupRoles', 'tryoutSetupRubrics', 'tryoutSetupRubric']) {
      invalidate([key, unref(seasonId), unref(tryoutId)])
    }
    void refreshEpisodeQueries(queryClient, ['programEpisodes', unref(clubId), unref(seasonId), unref(tryoutId)]).catch(() => undefined)
    invalidate(['journalWorkspace', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['journalSurveys', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['tryoutHome', unref(seasonId), unref(tryoutId), unref(clubId)])
    invalidate(['athleteJournal', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['journalRewards', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['scoresheetScans', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['journalRewardSettings', unref(clubId), unref(seasonId), unref(tryoutId)])

    const currentClubId = unref(clubId)
    const currentSeasonId = unref(seasonId)
    const currentTryoutId = unref(tryoutId)

    if (['participants', 'events', 'attributes', 'questions'].includes(payload.domain || '')) {
      invalidate(['participantImportMetadata', currentSeasonId, currentTryoutId, currentClubId])
    }

    switch (payload.domain) {
      case 'sessions':
        invalidate(tryoutContextQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['tryoutHome', currentSeasonId, currentTryoutId, currentClubId || ''])
        invalidate(tryoutParticipantsQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['tryoutSetupSessions', currentSeasonId, currentTryoutId])
        invalidate(tryoutCaptureQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['playerSkillAggregates', currentClubId, currentSeasonId, currentTryoutId])
        break

      case 'events':
      case 'attributes':
        invalidate(['tryoutSetupStaff', currentSeasonId, currentTryoutId])
        invalidate(['tryoutSetupRubrics', currentSeasonId, currentTryoutId])
        invalidate(['tryoutSetupRubric', currentSeasonId, currentTryoutId])
        invalidate(['seasonEvents', currentClubId, currentSeasonId])
        invalidate(tryoutContextQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['tryoutHome', currentSeasonId, currentTryoutId, currentClubId || ''])
        invalidate(tryoutParticipantsQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(tryoutCaptureQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['playerSkillAggregates', currentClubId, currentSeasonId, currentTryoutId])
        break

      case 'participants':
        invalidate(tryoutCaptureQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['tryoutHome', currentSeasonId, currentTryoutId, currentClubId || ''])
        invalidate(tryoutParticipantsQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidateParticipantDetail(payload.participantId)
        invalidate(['tryoutTeamAllocations', currentClubId, currentSeasonId, currentTryoutId])
        invalidate(['tryoutRecommendations', currentClubId, currentSeasonId, currentTryoutId])
        break

      case 'attendance':
      case 'comments':
        invalidate(tryoutParticipantsQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidateParticipantDetail(payload.participantId)
        invalidate(tryoutCaptureQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['playerSkillAggregates', currentClubId, currentSeasonId, currentTryoutId])
        break

      case 'roles':
      case 'scores':
      case 'feedbackSelections':
        if (payload.domain === 'roles') invalidate(tryoutContextQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(tryoutParticipantsQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['playerSkillAggregates', currentClubId, currentSeasonId, currentTryoutId])
        invalidate(tryoutCaptureQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['tryoutRecommendations', currentClubId, currentSeasonId, currentTryoutId])
        invalidate(['tryoutTeamAllocations', currentClubId, currentSeasonId, currentTryoutId])
        break

      case 'teamAllocations':
        invalidate(tryoutCaptureQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(tryoutParticipantsQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['tryoutTeamAllocations', currentClubId, currentSeasonId, currentTryoutId])
        invalidate(['tryoutRecommendations', currentClubId, currentSeasonId, currentTryoutId])
        break
    }
  }

  const stopRoomWatch = watch(room, (nextRoom, previousRoom) => {
    if (previousRoom) appSocket.leaveRoom(previousRoom)
    if (nextRoom) appSocket.joinRoom(nextRoom)
  }, { immediate: true })

  const refreshAfterConnect = () => {
    if (!room.value) return
    invalidate(['eventNotifications', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['journalRewards', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['scoresheetScans', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['athleteFeedback', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['demoJournalPreview', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['assessmentReadiness', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['assessmentFocus', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(tryoutCaptureQueryKey(unref(clubId), unref(seasonId), unref(tryoutId)))
    invalidate(['captureSelectorGroups', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['spaceMembershipHistory', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['manualParticipantAssociations', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['tryoutCaptureAnalysis', unref(clubId), unref(seasonId), unref(tryoutId)])
  }

  onMounted(() => {
    appSocket.on('connect', refreshAfterConnect)
    appSocket.on('tryout.context.changed', handleContextChanged)
    appSocket.on('tryout.scores.changed', handleScoresChanged)
  })

  onUnmounted(() => {
    stopRoomWatch()
    if (room.value) appSocket.leaveRoom(room.value)
    appSocket.off('connect', refreshAfterConnect)
    appSocket.off('tryout.context.changed', handleContextChanged)
    appSocket.off('tryout.scores.changed', handleScoresChanged)
  })

  function handleScoresChanged(payload: TryoutContextChangedPayload) {
    if (payload.domain !== 'scores') return
    if (!matchesCurrentTryout(payload, unref(clubId), unref(seasonId), unref(tryoutId))) return
    invalidate(['tryoutCaptureAnalysis', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(tryoutCaptureQueryKey(unref(clubId), unref(seasonId), unref(tryoutId)))
    invalidate(['assessmentReadiness', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['assessmentFocus', unref(clubId), unref(seasonId), unref(tryoutId)])
  }
}

function matchesCurrentTryout(
  payload: TryoutContextChangedPayload,
  clubId: string,
  seasonId: string,
  tryoutId: string
) {
  return String(payload.clubId || '') === clubId &&
    String(payload.seasonId || '') === seasonId &&
    String(payload.tryoutId || '') === tryoutId
}
